/**
 * What an invoice says in a list: number, status, reference and party, the
 * issue and due dates with how far off the due date is, and the amount due
 * with its VAT. Overdue is judged on the London calendar day. Shared by the
 * web invoice list on phones and the native app.
 */
import { format } from 'date-fns';
import { invoiceDueLabel } from '../../features/invoices/invoiceDueLabel';
import { chasedLabel } from '../../features/interim/chaseLog';
import type { Invoice } from '../../types/accounting';
import type { DetailRow } from '../projectDetail/projectOverviewModel';

export const formatPounds = (amount: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

/** Monetary values stay in their stored currency; this formatter never converts them. */
export function formatInvoiceMoney(amount: number, currency = 'GBP', currencyDisplay: 'symbol'|'code' = 'symbol'): string {
  const code = currency.trim().toUpperCase() || 'GBP';
  try {
    return new Intl.NumberFormat('en-GB', {style:'currency', currency:code, currencyDisplay, minimumFractionDigits:2, maximumFractionDigits:2}).format(amount);
  } catch {
    return `${code} ${new Intl.NumberFormat('en-GB', {minimumFractionDigits:2, maximumFractionDigits:2}).format(amount)}`;
  }
}

function formatInvoiceDate(date: string): string {
  try { return format(new Date(date), 'dd-MM-yyyy'); } catch { return date; }
}

/** Today's date in London as YYYY-MM-DD. */
export function londonToday(now = new Date()): string {
  return now.toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
}

export function isInvoiceOverdue(inv: Pick<Invoice, 'status' | 'dueDate'>, today = londonToday()): boolean {
  if (inv.status === 'overdue') return true;
  if (inv.status !== 'sent' && inv.status !== 'approved') return false;
  if (!inv.dueDate) return false;
  return inv.dueDate.slice(0, 10) < today;
}

export type InvoiceStatusTone = 'good' | 'neutral' | 'danger' | 'muted';

export function invoiceStatusTone(status: Invoice['status']): InvoiceStatusTone {
  if (status === 'paid' || status === 'approved') return 'good';
  if (status === 'overdue') return 'danger';
  if (status === 'void') return 'muted';
  return 'neutral';
}

export interface InvoiceRowModel {
  id: string;
  number: string;
  status: string;
  statusTone: InvoiceStatusTone;
  reference?: string;
  party: string;
  dates: string;
  overdue: boolean;
  amount: string;
  vat?: string;
  /** "Chased 2 days ago by Sam"; only for a sales invoice still awaiting payment. */
  chased?: string;
}

/** Statuses where a colleague may still be chasing the customer; paid, draft and void invoices never show a chase. */
const CHASEABLE: ReadonlySet<Invoice['status']> = new Set(['sent', 'approved', 'overdue']);

export function buildInvoiceRow(
  inv: Invoice,
  today = londonToday(),
  chase?: { chasedAt: string; chasedByName: string },
  now = new Date(),
): InvoiceRowModel {
  const due = invoiceDueLabel(inv, today);
  return {
    id: inv.id ?? inv.invoiceNumber,
    number: inv.invoiceNumber,
    status: inv.status,
    statusTone: invoiceStatusTone(inv.status),
    reference: inv.reference || undefined,
    party: inv.supplierOrCustomerName,
    dates: [`Issued ${formatInvoiceDate(inv.issueDate)}`, inv.dueDate && `Due ${formatInvoiceDate(inv.dueDate)}`, due].filter(Boolean).join(' · '),
    overdue: isInvoiceOverdue(inv, today),
    amount: formatInvoiceMoney(inv.amountDue ?? inv.total, inv.currency),
    vat: inv.vatAmount > 0 ? `inc. ${formatInvoiceMoney(inv.vatAmount, inv.currency)} VAT` : undefined,
    chased: chase && CHASEABLE.has(inv.status) ? chasedLabel(chase, now) : undefined,
  };
}

/** The invoice's money, as the detail sheet lists it. */
export function invoiceTotalRows(inv: Invoice): DetailRow[] {
  return [
    { label: 'Subtotal', value: formatInvoiceMoney(inv.subtotal, inv.currency), inline: true },
    { label: 'VAT', value: formatInvoiceMoney(inv.vatAmount, inv.currency), inline: true },
    ...(inv.cisDeduction ? [{ label: 'CIS deduction', value: formatInvoiceMoney(inv.cisDeduction, inv.currency), inline: true }] : []),
    { label: 'Total', value: formatInvoiceMoney(inv.total, inv.currency), inline: true },
    ...(inv.amountDue != null ? [{ label: 'Amount due', value: formatInvoiceMoney(inv.amountDue, inv.currency), inline: true }] : []),
  ];
}

/** Outstanding (not yet overdue), overdue, and paid since the start of this month. */
export function summariseInvoices(invoices: Invoice[], now = new Date()) {
  const today = londonToday(now);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const owed = (inv: Invoice) => inv.amountDue ?? inv.total;
  return {
    outstanding: invoices.filter((inv) => (inv.status === 'sent' || inv.status === 'approved') && !isInvoiceOverdue(inv, today)).reduce((sum, inv) => sum + owed(inv), 0),
    overdue: invoices.filter((inv) => isInvoiceOverdue(inv, today)).reduce((sum, inv) => sum + owed(inv), 0),
    paidThisMonth: invoices.filter((inv) => inv.status === 'paid' && inv.updatedAt && new Date(inv.updatedAt) >= monthStart).reduce((sum, inv) => sum + inv.total, 0),
  };
}


/** Separate currencies because no exchange-rate conversion is present in invoice totals. */
export function summariseInvoicesByCurrency(invoices: Invoice[], now = new Date()) {
  const groups = new Map<string, Invoice[]>();
  for (const invoice of invoices) {
    const currency = invoice.currency?.trim().toUpperCase() || 'GBP';
    const rows = groups.get(currency) ?? [];
    rows.push(invoice);
    groups.set(currency, rows);
  }
  if (!groups.size) groups.set('GBP', []);
  return [...groups]
    .sort(([a], [b]) => a === 'GBP' ? -1 : b === 'GBP' ? 1 : a.localeCompare(b))
    .map(([currency, rows]) => ({currency, ...summariseInvoices(rows, now)}));
}
